import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Briefcase, Users, ArrowUpRight, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { DeleteJobDialog } from "../components/DeleteJobDialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const [jobs, setJobs] = useState(null);
  const [credits, setCredits] = useState(null);
  const [orgFilter, setOrgFilter] = useState("all");
  const { activeWorkspace } = useAuth();
  const isAdmin = activeWorkspace?.role === "admin";

  const loadJobs = useCallback(() => {
    api.get("/jobs").then(({ data }) => setJobs(data));
    api.get("/billing/credits").then(({ data }) => setCredits(data));
  }, []);

  useEffect(() => {
    if (!activeWorkspace) return;
    loadJobs();
  }, [activeWorkspace, loadJobs]);

  const organizations = jobs ? [...new Set(jobs.map((j) => j.organization).filter(Boolean))] : [];
  const filteredJobs = jobs ? (orgFilter === "all" ? jobs : jobs.filter((j) => j.organization === orgFilter)) : [];

  return (
    <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Jobs</h1>
          <p className="mt-1 text-sm text-muted-foreground">Evaluate resumes against your job requirements</p>
        </div>
        {isAdmin && (
          <div className="text-right">
            <Button asChild size="lg" data-testid="create-job-button">
              <Link to="/app/jobs/new">
                <Plus className="h-4 w-4" /> New Job
              </Link>
            </Button>
            {credits && (
              <p className="mt-1.5 text-xs text-muted-foreground" data-testid="job-quota-label">
                {credits.jobs_used}/{credits.max_jobs} job slots used
              </p>
            )}
          </div>
        )}
      </div>

      {organizations.length > 1 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="organization-filter">
          <button
            onClick={() => setOrgFilter("all")}
            data-testid="org-filter-all"
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
              orgFilter === "all" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            All Organizations
          </button>
          {organizations.map((org) => (
            <button
              key={org}
              onClick={() => setOrgFilter(org)}
              data-testid={`org-filter-${org}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                orgFilter === org ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {org}
            </button>
          ))}
        </div>
      )}

      {jobs === null ? (
        <div className="mt-10 text-sm text-muted-foreground">Loading jobs...</div>
      ) : jobs.length === 0 ? (
        <div
          className="mt-16 flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-20 text-center"
          data-testid="empty-jobs-state"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <Briefcase className="h-6 w-6 text-primary" />
          </div>
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            {isAdmin ? "No jobs yet. Create your first job to start evaluating resumes." : "No jobs yet. Ask an admin to create a job."}
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
          {filteredJobs.map((job, idx) => (
            <div
              key={job.id}
              style={{ animationDelay: `${idx * 60}ms` }}
              className="group animate-in fade-in-0 slide-in-from-bottom-2 relative"
            >
              {isAdmin && (
                <DeleteJobDialog job={job} candidateCount={job.candidate_count} onDeleted={loadJobs}>
                  <button
                    type="button"
                    title="Delete job"
                    aria-label={`Delete ${job.title}`}
                    data-testid={`delete-job-btn-${job.id}`}
                    className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground opacity-0 transition-all duration-200 hover:bg-destructive/10 hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </DeleteJobDialog>
              )}
              <Link
                to={`/app/jobs/${job.id}`}
                data-testid={`job-card-${job.id}`}
                className="block overflow-hidden rounded-2xl border border-border/80 bg-card p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Briefcase className="h-5 w-5 text-primary" />
                  </div>
                  {!isAdmin && (
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                  )}
                </div>
                <h3 className="mt-4 font-heading font-semibold leading-snug">{job.title}</h3>
                {job.organization && (
                  <p className="mt-0.5 text-xs font-medium text-primary">{job.organization}</p>
                )}
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{job.jd_text}</p>
                <div className="mt-5 flex items-center gap-1.5 border-t border-border/60 pt-4 text-xs font-medium text-muted-foreground">
                  <Users className="h-3.5 w-3.5" /> {job.candidate_count} candidate(s) evaluated
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
